/**
 * FF Postal Check — site-wide local-delivery postal-code checker.
 *
 * STATIC LOOKUP ONLY — zero network calls.
 * The served-FSA -> local delivery price map is baked into the page by
 * snippets/ff-postal-check.liquid (script#ff-postal-map), which is generated from
 * `shopify-local-delivery-coverage/scripts/probe-delivery-coverage.py` and must be
 * regenerated whenever delivery zones change in Shopify Admin.
 *
 * Never add a browser network call (fetch or XHR) or a storefront JSON / shipping-rate probe
 * to this file: those endpoints are IP-rate-limited (HTTP 429, local_rate_limited) and a rate
 * probe mutates the customer's cart. The component is therefore safe on every template,
 * cart included.
 *
 * Accepted input (normalized first: uppercase, non-alphanumerics stripped):
 *   6 chars — full Canadian postal code, e.g. "L3X 1A1" / "l3x1a1" (letter-digit-letter-digit-letter-digit)
 *   3 chars — bare FSA shorthand, e.g. "L3X", so the strip can answer from an FSA alone
 * Anything else is an inline field error and never reaches the lookup.
 *
 * The letter alphabet is Canada Post's (PCCF reference guide): a postal code never uses the
 * letters D, F, I, O, Q or U, and the first character additionally excludes W and Z. The FSA is
 * alpha-numeric-alpha and the LDU is numeric-alpha-numeric, so the letter slots are 1, 3 and 5
 * — position 3 is a LETTER ("L3X"), not a digit. Without this restriction an impossible code
 * such as "L3X 1F1" would validate and report the L3X delivery price.
 *
 * The single intentional global is window.FFPostalCheck (test handles). Everything else
 * lives inside the IIFE, and DOM wiring is skipped in Node so the logic is unit-testable.
 */
(function () {
  'use strict';

  var DEFAULT_PHONE = '647-323-7312';
  var DELIVERY_AREAS_HREF = '/pages/delivery-areas';
  var DELIVERY_AREAS_LABEL = 'See delivery areas';
  // Canada Post letter alphabet (see the header): 20 letters with no D/F/I/O/Q/U, and 18 once
  // W/Z are removed from the first character.
  var POSTAL_LETTER = '[ABCEGHJKLMNPRSTVWXYZ]';
  var POSTAL_FIRST_LETTER = '[ABCEGHJ-NPRSTVXY]';
  var FULL_POSTAL_CODE_PATTERN = new RegExp(
    '^' + POSTAL_FIRST_LETTER + '\\d' + POSTAL_LETTER + '\\d' + POSTAL_LETTER + '\\d$'
  );
  var BARE_FSA_PATTERN = new RegExp('^' + POSTAL_FIRST_LETTER + '\\d' + POSTAL_LETTER + '$');

  // Copy is law: "requested delivery time" only. Urgent same-day requests are handled by
  // phone at the number below — never promised as an online option. Keep each template on a
  // single line so tests/postal-check-static-checks.js can assert the exact shipped copy.
  //
  // Round 12 (G4) — NO DOLLAR FIGURE. This used to print "Local delivery ${PRICE}" from the
  // hand-baked served-FSA map, which has no tie to the rates Shopify actually charges at
  // checkout: a stale map entry published a price the checkout then contradicted, and the
  // shopper only found out at payment. The served verdict now states the FACT (local delivery
  // is available, priced at checkout) and points at /pages/delivery-areas; the requested-time
  // line and the phone fallback stay. Nothing was added to replace the number — the map still
  // decides WHICH FSAs are served (that is why it stays), it just no longer prints a rate.
  //
  // Length budget (measured in-page on the live component, 2026-11-01):
  //   - >= 1024px the verdict box is clamped to 2 lines at the 800px measure: the sentence
  //     plus the inline link measures exactly 2 lines at 1280/1536 and falls to 3 at 768-1023,
  //     where the clamp is 3. The longer "for same-day or a specific time" phrasing was
  //     measured at 3 lines/1024 and 4 lines/768 and is therefore NOT shippable here — it
  //     would be cut. Hence "same-day, call {PHONE}".
  //   - < 768px the verdict is unclamped and owns its own row; the shipped phone copy plus the
  //     INLINE link measures 4 lines / 68.2px at 320px against the prompt's 18.75px line box,
  //     i.e. +49.4px, inside the 52px phone growth budget the spec asserts.
  var SERVED_TEMPLATE = 'Yes — we deliver to {FSA}. Local delivery available at checkout. Online orders are scheduled for the requested delivery time; same-day, call {PHONE}.';
  // The phone-only served copy, used below 768px where the band's verdict row is too narrow
  // for the approved sentence. This is the same verdict in a shorter, self-contained form: the
  // same three facts (we deliver to your FSA, local delivery is priced at checkout, the
  // requested-delivery-time rule) and the same same-day rule (phone-only, beside the number).
  // The copy law applies to it exactly as it does to SERVED_TEMPLATE: no midday deadline,
  // no countdown, no free-delivery claim, no online same-day promise. Four lines at 320px
  // WITH the inline delivery-areas link.
  var SERVED_MOBILE_TEMPLATE = 'Yes — we deliver to {FSA}. Local delivery available at checkout. The requested delivery time applies; same-day, call {PHONE}.';
  // The snippet's stacked mobile band (@media (max-width: 767px)) is the breakpoint the served copy
  // switches on, so the two must stay in step: SERVED_MOBILE_TEMPLATE is the copy that fits there.
  var MOBILE_QUERY = '(max-width: 767px)';
  // The not-served copy ships as TWO parts, not one sentence: below 768px the band's text row is
  // a two-line budget (the +16px the strip may grow by), and the recovery line has to be fully
  // visible inside it. Part 1 is its own line ("We don't deliver to L5A yet."), part 2 is the
  // second line — the phone, then the delivery-areas link (ACTION_TEMPLATE + link.label) — so the
  // link renders whole instead of being ellipsised mid-label by the height clamp. Each part is one
  // line at 320px; tests/postal-check.spec.js asserts scrollHeight - clientHeight <= 1 at 320/375.
  var NOT_SERVED_TEMPLATE = "We don't deliver to {FSA} yet.";
  var NOT_SERVED_ACTION_TEMPLATE = 'Call {PHONE} · ';
  // Round 9 (D6): the worked example is a postal code that is NOT one of the FSAs
  // this store delivers to. It used to be "L3X 1A1" — the store's own Newmarket
  // FSA — so the invalid-input error advertised a served code and the round-8 fix
  // that took it out of the field's placeholder never reached the copy a shopper
  // actually reads after a bad submit. M5V (Toronto) is valid Canada Post and is
  // absent from the served-FSA map above, so the example cannot be mistaken for a
  // quoted local delivery price.
  var INVALID_TEMPLATE = 'Enter a valid Canadian postal code, e.g. M5V 3L9';

  /** "+1 6473237312" / "16473237312" -> "647-323-7312"; anything unusable -> ''. */
  function formatPhone(value) {
    var digits = String(value === null || value === undefined ? '' : value).replace(/[^0-9]/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length !== 10) return '';
    return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6, 10);
  }

  /** Uppercase, strip spaces/hyphens/punctuation: " l3x-1a1 " -> "L3X1A1". */
  function normalizePostalCode(value) {
    return String(value === null || value === undefined ? '' : value)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  /** First three characters of a normalized postal code (the FSA, e.g. L3X). */
  function getFsa(normalized) {
    return String(normalized === null || normalized === undefined ? '' : normalized).slice(0, 3);
  }

  function isFullPostalCode(normalized) {
    return FULL_POSTAL_CODE_PATTERN.test(normalized);
  }

  function isBareFsa(normalized) {
    return BARE_FSA_PATTERN.test(normalized);
  }

  function fillTemplate(template, values) {
    return template.replace(/\{([A-Z]+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /**
   * Tidy bulk-inserted text (paste / autofill / drag-drop): drop punctuation, collapse
   * whitespace and, when the text starts with a postal code, keep just that code.
   * " L3X 1A1 " -> "L3X 1A1"; "L3X 1A1, Newmarket" -> "L3X 1A1"; "l3x-1a1" -> "L3X 1A1".
   * Keeps the field inside maxlength="7" so a pasted leading space cannot eat a character.
   * Uses the same Canada Post letter alphabet as the validators above so the two cannot drift.
   */
  var CLEAN_POSTAL_INPUT_PATTERN = new RegExp(
    '^(' + POSTAL_FIRST_LETTER + '\\d' + POSTAL_LETTER + ')(?: ?(\\d' + POSTAL_LETTER + '\\d))?',
    'i'
  );

  function cleanPostalInput(value) {
    var cleaned = String(value === null || value === undefined ? '' : value)
      .replace(/[^A-Za-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var match = cleaned.match(CLEAN_POSTAL_INPUT_PATTERN);
    if (match) return (match[2] ? match[1] + ' ' + match[2] : match[1]).toUpperCase();
    return cleaned.slice(0, 7).toUpperCase();
  }

  /**
   * Build a checker bound to a served-FSA -> price map.
   *
   * evaluate(raw) returns one of:
   *   { state: 'served',     fsa, price, message, mobileMessage, action: null,     link: { href, label } }
   *   { state: 'not_served', fsa, price: null, message, action, link: { href, label } }
   *   { state: 'invalid',    fsa: '', price: null, message, action: null, link: null }
   *
   * `mobileMessage` is the served verdict as the phone-only copy (see SERVED_MOBILE_TEMPLATE); on
   * every other state it is null and `message` is the only copy. The DOM layer picks between them
   * for the current breakpoint — the lookup itself stays viewport-free and unit-testable.
   *
   * Round 12 (G4): EVERY state now carries the /pages/delivery-areas link and NO state publishes a
   * dollar figure. `price` is still returned (the map is still the served-FSA source of truth and
   * the unit tests read the tiers) but no copy interpolates it any more — the checkout owns the
   * real rate. The served link is the trailing element of the SAME sentence, so the DOM layer keeps
   * it inline; the not-served link is a recovery line and keeps its own block line below 768px.
   *
   * `action` is the recovery line that follows the message in the same live region: the
   * not-served state uses it so the phone + link can be put on their own line below 768px
   * (see NOT_SERVED_ACTION_TEMPLATE). The served state has no recovery line, which is exactly how
   * the DOM layer tells the two links apart (result.action presence).
   */
  function createPostalChecker(map, options) {
    var table = map && typeof map === 'object' ? map : {};
    var settings = options || {};
    var phone = formatPhone(settings.phone) || DEFAULT_PHONE;
    var areasHref = settings.deliveryAreasHref || DELIVERY_AREAS_HREF;
    var areasLabel = settings.deliveryAreasLabel || DELIVERY_AREAS_LABEL;

    function evaluate(raw) {
      var normalized = normalizePostalCode(raw);
      if (!isFullPostalCode(normalized) && !isBareFsa(normalized)) {
        return { state: 'invalid', fsa: '', price: null, message: INVALID_TEMPLATE, mobileMessage: null, action: null, link: null };
      }

      var fsa = getFsa(normalized);
      var price = Object.prototype.hasOwnProperty.call(table, fsa) && table[fsa]
        ? String(table[fsa])
        : null;

      if (price) {
        var values = { FSA: fsa, PHONE: phone };
        return {
          state: 'served',
          fsa: fsa,
          price: price,
          message: fillTemplate(SERVED_TEMPLATE, values),
          mobileMessage: fillTemplate(SERVED_MOBILE_TEMPLATE, values),
          action: null,
          link: { href: areasHref, label: areasLabel },
        };
      }

      return {
        state: 'not_served',
        fsa: fsa,
        price: null,
        message: fillTemplate(NOT_SERVED_TEMPLATE, { FSA: fsa, PHONE: phone }),
        mobileMessage: null,
        action: fillTemplate(NOT_SERVED_ACTION_TEMPLATE, { PHONE: phone }),
        link: { href: areasHref, label: areasLabel },
      };
    }

    return { evaluate: evaluate, phone: phone, map: table };
  }

  var api = {
    DEFAULT_PHONE: DEFAULT_PHONE,
    DELIVERY_AREAS_HREF: DELIVERY_AREAS_HREF,
    DELIVERY_AREAS_LABEL: DELIVERY_AREAS_LABEL,
    INVALID_MESSAGE: INVALID_TEMPLATE,
    SERVED_MESSAGE: SERVED_TEMPLATE,
    SERVED_MOBILE_MESSAGE: SERVED_MOBILE_TEMPLATE,
    MOBILE_QUERY: MOBILE_QUERY,
    NOT_SERVED_MESSAGE: NOT_SERVED_TEMPLATE,
    NOT_SERVED_ACTION_MESSAGE: NOT_SERVED_ACTION_TEMPLATE,
    formatPhone: formatPhone,
    normalizePostalCode: normalizePostalCode,
    cleanPostalInput: cleanPostalInput,
    getFsa: getFsa,
    isFullPostalCode: isFullPostalCode,
    isBareFsa: isBareFsa,
    createPostalChecker: createPostalChecker,
  };

  if (typeof window !== 'undefined') window.FFPostalCheck = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  // Node (unit tests): expose the pure logic and stop before touching the DOM.
  if (typeof document === 'undefined') return;

  function readMap() {
    var node = document.getElementById('ff-postal-map');
    if (!node) return {};
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  /** True when the band is in its stacked mobile layout (the copy breakpoint), see MOBILE_QUERY. */
  function prefersMobileCopy() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function renderResult(root, region, input, result) {
    region.className = 'ff-postal-check__result is-' + result.state.replace(/_/g, '-');
    while (region.firstChild) region.removeChild(region.firstChild);
    // The served verdict ships in two lengths (see SERVED_TEMPLATE / SERVED_MOBILE_TEMPLATE): the
    // phone band is too narrow for the approved sentence, so it renders the self-contained phone
    // copy that fits whole. Every other state has a single copy.
    var message = result.mobileMessage && prefersMobileCopy() ? result.mobileMessage : result.message;
    region.appendChild(document.createTextNode(message));

    if (result.action || result.link) {
      // The recovery line (phone + link) rides its own element so it can be broken onto its own
      // line below 768px — `.ff-postal-check__result-action { display: block }` in the mobile
      // block of the snippet — while it stays in the sentence flow at >= 768px. The class
      // therefore must not be dropped: it is what keeps the not-served link inside the band's
      // two-line budget instead of being ellipsised by the height clamp.
      var action = document.createElement('span');
      action.className = 'ff-postal-check__result-action';
      // Round 12 (G4): the SERVED verdict's link (no recovery line) is the trailing element of the
      // same sentence, not a second line, so it stays inline at EVERY breakpoint. That is what keeps
      // the phone verdict at four lines / +49.4px instead of five lines / +66px, which is outside
      // the 52px growth budget the spec asserts at 320px. The not-served pair (phone + link) keeps
      // the mobile block line so the link label can never be ellipsised mid-word.
      if (!result.action) action.classList.add('ff-postal-check__result-action--inline');
      if (result.action) action.appendChild(document.createTextNode(result.action));
      if (result.link) {
        var anchor = document.createElement('a');
        anchor.href = result.link.href;
        anchor.textContent = result.link.label;
        action.appendChild(anchor);
      }
      // A space between the message and the action: it is a collapsed trailing space while the
      // action is a block (mobile) and the sentence's own separator while it is inline (>= 768px).
      region.appendChild(document.createTextNode(' '));
      region.appendChild(action);
    }

    // Below 1024px the verdict replaces the prompt in the band's own line box (the positive
    // state is a class rather than a CSS :has() lookup so the prompt and the verdict can never
    // both paint in the one grid cell that holds them).
    if (root) root.classList.add('has-result');

    if (input) {
      var invalid = result.state === 'invalid';
      input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (invalid) {
        input.classList.add('is-invalid');
      } else {
        input.classList.remove('is-invalid');
      }
    }
  }

  function init() {
    var root = document.getElementById('ff-postal-check');
    if (!root) return;

    // `const` (not `var`) on purpose: the reset handler below closes over these, and only a
    // const binding keeps TypeScript's non-null narrowing valid inside the closure.
    const form = root.querySelector('.ff-postal-check__form');
    const input = /** @type {HTMLInputElement} */ (root.querySelector('.ff-postal-check__input'));
    const region = root.querySelector('.ff-postal-check__result');
    // Round 12 (G5): the reset affordance. It ships `hidden` and is revealed by the first
    // verdict; before it existed a verdict replaced the prompt line for the rest of the
    // session with no way back (the prompt only returns on a fresh page load).
    const reset = /** @type {HTMLButtonElement|null} */ (root.querySelector('.ff-postal-check__reset'));
    if (!form || !input || !region) return;

    var checker = createPostalChecker(readMap(), { phone: root.getAttribute('data-phone') });
    /** @type {ReturnType<typeof checker.evaluate> | null} */
    var lastResult = null;

    /** @param {ReturnType<typeof checker.evaluate>} result */
    function show(result) {
      lastResult = result;
      renderResult(root, region, input, result);
      if (reset) reset.hidden = false;
    }

    /**
     * Restore the prompt state: clear the live region, drop the band's `has-result` state
     * (which is what put the verdict in the prompt's own grid cell), clear the field and hand
     * focus back to it. The reset button hides itself again because there is nothing left to
     * clear — its own row never changed height, so the band cannot grow here.
     */
    function clearVerdict() {
      lastResult = null;
      while (region.firstChild) region.removeChild(region.firstChild);
      region.className = 'ff-postal-check__result';
      root.classList.remove('has-result');
      input.value = '';
      input.classList.remove('is-invalid');
      input.setAttribute('aria-invalid', 'false');
      if (reset) reset.hidden = true;
      // The button that was just hidden owned focus; hand it to the field the shopper is
      // meant to type into rather than letting focus fall back to <body>.
      input.focus();
    }

    if (reset) reset.addEventListener('click', clearVerdict);

    form.addEventListener('submit', function (event) {
      // Real form + preventDefault, so Enter and the button both submit in place.
      event.preventDefault();
      show(checker.evaluate(input.value));
    });

    // The served copy is chosen for the current breakpoint, so a rotate or a resize across the
    // 767px line re-paints the SAME verdict in the copy that fits the band. Only the last verdict
    // is re-rendered, through the same path, so the prompt swap and the invalid styling come along.
    if (window.matchMedia) {
      var mobileQuery = window.matchMedia(MOBILE_QUERY);
      var onBreakpoint = function () {
        if (lastResult) renderResult(root, region, input, lastResult);
      };
      if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onBreakpoint);
      else if (mobileQuery.addListener) mobileQuery.addListener(onBreakpoint);
    }

    input.addEventListener('beforeinput', function (event) {
      var data = event.data;
      // Only bulk insertions (paste / autofill) need cleaning. Single keystrokes pass
      // through untouched so a shopper can type "L3X 1A1" one character at a time.
      if (!data || data.length < 3) return;
      var cleaned = cleanPostalInput(data);
      if (cleaned === data) return;
      event.preventDefault();
      input.setRangeText(cleaned, /** @type {number} */ (input.selectionStart), /** @type {number} */ (input.selectionEnd), 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    input.addEventListener('input', function () {
      // A pasted leading space would be counted by maxlength="7" and silently eat the last
      // character (" L3X 1A1 " -> " L3X 1A"), so drop leading whitespace as it arrives.
      if (/^\s/.test(input.value)) input.value = input.value.replace(/^\s+/, '');

      // Clear the error styling as the shopper corrects the field; the live region keeps
      // its last message so screen readers are not spammed on every keystroke.
      if (!input.classList.contains('is-invalid')) return;
      input.classList.remove('is-invalid');
      input.setAttribute('aria-invalid', 'false');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
